/**
 * Imperative bridge to react-admin's notification system for NON-React callers.
 *
 * `useNotify()` is a hook and can only be used inside the component tree, but a
 * few call sites need to raise a toast from outside it — notably
 * `authProvider.checkError`, which handles a 403 by notifying the user while
 * keeping the session alive (#4265 AC#3).
 *
 * A component mounted inside `<Admin>` (see `NotificationListener`) registers
 * react-admin's `notify` here via {@link setImperativeNotify}.
 *
 * #3804: calls that arrive BEFORE that registration used to be dropped with a
 * `console.warn`. The real pre-mount window is a `checkError`-driven 403 raised
 * during the `requireAuth` gate — react-admin resolves auth and can issue
 * dataProvider calls before `Layout` (and therefore `NotificationListener`)
 * mounts, and any 403 in that window was silently discarded. We now hold those
 * messages and flush them when a notify registers.
 *
 * NOT the mechanism, despite an earlier version of this comment saying so: the
 * boot-path partner fetches cannot reach here at all. `msalAuthProvider.ts:268`
 * and `:384` are raw `fetch()` calls guarded by a bare `if (partnerResponse.ok)`
 * with no `else`, and `partnerResolution.ts:179` is likewise a raw fetch. They
 * bypass `httpClient`, so react-admin never invokes `authProvider.checkError`
 * and `on403` never runs. A 403 on those calls is swallowed by the `.ok` guard —
 * a separate bug that buffering here cannot fix.
 */
import type { useNotify } from "react-admin";

type NotifyFn = ReturnType<typeof useNotify>;
type NotifyArgs = Parameters<NotifyFn>;

let notifyRef: NotifyFn | null = null;

/**
 * Cap on held messages. A pre-mount failure storm (several resources 403ing at
 * once) must not turn into an unbounded array or a wall of toasts on mount;
 * beyond this we drop the OLDEST, since the newest failure is the most
 * diagnostic. Small because these are user-facing toasts, not a log.
 */
const MAX_PENDING = 5;

/**
 * How long a held message stays worth showing. A toast that surfaces a minute
 * after the fact is confusing rather than helpful — it would appear detached
 * from the action that caused it — so stale entries are dropped at flush time.
 */
const PENDING_TTL_MS = 30_000;

interface PendingNotification {
  args: NotifyArgs;
  queuedAt: number;
}

let pending: PendingNotification[] = [];

/**
 * Register (or clear, with `null`) react-admin's `notify` for imperative use.
 *
 * Registering flushes anything held while no listener was mounted. Clearing
 * discards the queue: `null` means the listener has unmounted (logout, or the
 * layout being torn down), and delivering those messages to a later, unrelated
 * mount would show the user a toast for an action they've moved on from.
 */
export const setImperativeNotify = (fn: NotifyFn | null): void => {
  notifyRef = fn;
  if (!fn) {
    pending = [];
    return;
  }
  const flushable = pending;
  pending = [];
  const now = Date.now();
  for (const { args, queuedAt } of flushable) {
    if (now - queuedAt > PENDING_TTL_MS) continue;
    fn(...args);
  }
};

/**
 * Raise a react-admin toast from outside the component tree. If no `notify` is
 * registered yet the message is held (see {@link setImperativeNotify}) rather
 * than dropped, so pre-mount failures still reach the user.
 */
export const notifyImperative: NotifyFn = ((...args: NotifyArgs) => {
  if (notifyRef) {
    notifyRef(...args);
    return;
  }
  pending.push({ args, queuedAt: Date.now() });
  if (pending.length > MAX_PENDING) pending.shift();
  console.warn("[imperativeNotify] notify not registered yet; holding message until it is:", args[0]);
}) as NotifyFn;

/** Test seam: number of messages currently held. */
export const __getPendingCount = (): number => pending.length;
